/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'
import {overrides as baseOverrides} from './base'
import dynamiteOverrides from './brands/dynamite'

// Brand-specific theme override mappings
const brandOverrides = {
    dynamite: dynamiteOverrides
}

/**
 * French typography rule: wherever English uses Title Case (`textTransform: 'capitalize'`),
 * French uses Sentence case. There is no CSS "sentence case" transform, so we drop the
 * capitalize transform (`'none'`) and let the already sentence-cased French source render
 * as authored. `uppercase` and `none` are left untouched. Applied recursively to the plain
 * override objects (before extendTheme), so it covers every `textStyles` entry and any
 * component style object; functions and primitives pass through unchanged.
 *
 * @param {*} node - a theme override value (object, array, or primitive)
 * @returns {*} a transformed copy for French locales
 */
const toSentenceCaseOverrides = (node) => {
    if (Array.isArray(node)) return node.map(toSentenceCaseOverrides)
    if (node && typeof node === 'object') {
        const out = {}
        for (const [key, value] of Object.entries(node)) {
            out[key] =
                key === 'textTransform' && value === 'capitalize'
                    ? 'none'
                    : toSentenceCaseOverrides(value)
        }
        return out
    }
    return node
}

const isFrenchLocale = (localeId) =>
    typeof localeId === 'string' && localeId.toLowerCase().startsWith('fr')

// Built on first use per (brand, casing) and cached. extendTheme deep-merges the whole
// component theme, so building all four variants up front cost every page load for the one it uses.
const themeCache = new Map()

const buildTheme = (brandId, isFrench) => {
    const base = isFrench ? toSentenceCaseOverrides(baseOverrides) : baseOverrides
    const overrides = brandOverrides[brandId]
    if (!overrides) return extendTheme(base)
    return extendTheme(base, isFrench ? toSentenceCaseOverrides(overrides) : overrides)
}

/**
 * Returns the Chakra theme for the given brand and locale, building it on first request.
 * French locales get the sentence-case typography variant. Falls back to the
 * base theme if the brand is not found.
 *
 * @param {string} brandId - e.g. 'dynamite'
 * @param {string} [localeId] - e.g. 'fr-CA' (French locales use sentence case)
 * @returns {object} Chakra UI theme object
 */
export const getThemeForBrand = (brandId, localeId) => {
    const isFrench = isFrenchLocale(localeId)
    const resolvedBrand = brandOverrides[brandId] ? brandId : 'base'
    const key = `${resolvedBrand}:${isFrench ? 'fr' : 'en'}`
    if (!themeCache.has(key)) themeCache.set(key, buildTheme(resolvedBrand, isFrench))
    return themeCache.get(key)
}

// Re-export the overrides for backward compatibility
export {overrides} from './base'
